import type { Breakpoint } from '@mui/material';
import { Dialog, DialogActions, DialogContent, DialogTitle, styled } from '@mui/material';
import * as React from 'react';

import ActionButton from 'Components/ActionButton';
import FormRow from 'Components/FormRow';
import { dialogActions, dialogTitle, dialogContent } from '../../../../../../config/theme/componentTheme';
import { Container, Item } from 'Components/Grid';
import InputHandler from 'Components/InputHandler';
import ActionButtonRounded from 'Components/ActionButtonRounded';
import colors from '../../../../../../config/theme/colors';
import { fm } from 'Components/FormatMessage';
import { ActionableErrorContext, Loggable, makeLog } from 'Utility/logger';
import SearchSpinner from 'Routes/_MainLander/components/SearchSpinner';
import { isPartialDate, nowPartialDate, parsePatientDemographics, partialDateFromDate } from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import {
  search,
  linkAvxPatient,
  fetchLocations,
  createAvxPatient,
  updateAvxPatient,
  fetchAvxPatientInfo,
  fetchAvxLocationUsers,
} from '../functions';
import type { TPatient, TProfileData, TLocation, TAvxLocationUser } from '../interfaces';
import { PAPTherapyContext } from 'Routes/Treatment/Document';
import { useSelector } from 'react-redux';
import ToolTip from 'Components/ToolTip';
import { deviceTypesToDeviceNamesMap } from './config';
import { useAppDispatch, useAppSelector } from 'Store/index';
import { loadAvxPatientLink } from 'Store/session';
import { isEmpty, omit } from 'Utility/ramdaReplacement';
import FormSection from 'Components/FormSection';

const StyledContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
});

const StartView = ({ active }: { active: boolean }): React.JSX.Element => {
  return (
    <React.Fragment>
      <FormRow headerWidth={1} title="treatment.papTherapy.resMedStatus">
        {fm(`treatment.papTherapy.resMed${active ? 'InUse' : 'NotInUse'}`)}
      </FormRow>
    </React.Fragment>
  );
};

const SearchView = ({
  setDialogOpen,
  setDialogState,
  deviceSerialNumber,
  setDeviceSerialNumber,
}: {
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setDialogState: React.Dispatch<React.SetStateAction<TDialogState>>;
  deviceSerialNumber: string;
  setDeviceSerialNumber: React.Dispatch<React.SetStateAction<string>>;
}): React.JSX.Element => {
  const dispatch = useAppDispatch();

  const [state, setState] = React.useState<TSearchState>(undefined);
  const [searchResult, setSearchResult] = React.useState<TSearchResult | undefined>(undefined);

  const patientData = useSelector((s: IState) => s.patient.data);

  const searchText = deviceSerialNumber;
  const setSearchText = setDeviceSerialNumber;

  const handleSearch = (): void => {
    setSearchResult(undefined);
    if (typeof deviceSerialNumber === 'string' && searchText.length > 0) {
      setState('searching');
      search(searchText)
        .then(res => {
          if (typeof res === 'object' && !isEmpty(res)) {
            const data = {
              ecn: res.ecn,
              firstName: res.firstName,
              lastName: res.lastName,
              dob: res.dob,
              location: res.location,
              setupDate: res.setupDate,
              device: res.prescription.device,
            };
            setTimeout(() => {
              setState('found');
            }, 200);
            setSearchResult(data);
          } else {
            setSearchResult(undefined);
            setTimeout(() => {
              setDialogState('edit');
            }, 200);
          }
        })
        .catch((err: unknown) => {
          setSearchResult(undefined);
          setTimeout(() => {
            setState(undefined);
          }, 200);
          makeLog(Loggable.BugActionable, err, new ActionableErrorContext(null, 'AVX patient search'));
          return null;
        });
    }
  };

  const handleEnter = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter' && typeof searchText === 'string' && searchText.length > 0) {
      handleSearch();
    }
  };

  // Used if there is a deviceType that should match an S9 device but no deviceTypeDesc
  const deviceName =
    searchResult?.device.deviceType &&
    searchResult.device.deviceType.length === 6 &&
    deviceTypesToDeviceNamesMap.has(searchResult.device.deviceType)
      ? deviceTypesToDeviceNamesMap.get(searchResult.device.deviceType)
      : undefined;

  return (
    <React.Fragment>
      <div style={{ marginBottom: '1.25rem' /**'3rem'*/ }}>
        <div>{fm('treatment.papTherapy.findPatientWithSerialNumber')}</div>
      </div>
      <Container style={{ alignItems: 'center' }}>
        <Item xs={10} style={{ paddingRight: '2rem' }} onKeyUp={handleEnter}>
          <InputHandler
            type="TextField"
            editing={true}
            name="searchText"
            formData={{
              onChange: (values: TOnChangeValues) => {
                const name = Object.keys(values)[0];
                if (!name) return;
                const value = values[name] as string;
                setSearchText(value);
                if (value !== searchText) {
                  setState(undefined);
                }
              },
              document: { searchText: searchText },
            }}
            fullWidth
            format={/^\d{0,11}$/}
            placeholder="treatment.papTherapy.enterSerialNumber"
          />
        </Item>
        <Item xs={2}>
          <ActionButtonRounded
            text="general.search"
            width={6}
            height={3}
            fontSize={16}
            onClick={() => handleSearch()}
            filled
            disabled={!!(typeof searchText !== 'string' || searchText.length !== 11)}
            disabledTooltip={fm('treatment.papTherapy.invalidSerialNumberMessage')}
            // uppercase={false}
          />
        </Item>
      </Container>
      {state ? (
        <React.Fragment>
          {
            {
              searching: (
                <Container style={{ height: '28rem', flexDirection: 'column', justifyContent: 'center' }}>
                  <Item xs={12}>
                    <SearchSpinner text="general.searching" />
                  </Item>
                </Container>
              ),
              found: searchResult ? (
                <StyledContainer style={{ justifyContent: 'space-between', minHeight: '28rem' }}>
                  <StyledContainer style={{ flex: 1, justifyContent: 'center', padding: '1rem 0 1rem 0' }}>
                    <div>{fm('treatment.papTherapy.connectExistingResMed')}</div>
                    <div
                      style={{
                        backgroundColor: colors.lightestGray,
                        height: 'min-content',
                        padding: '1rem',
                        marginTop: '0.5rem',
                      }}
                    >
                      <FormRow headerWidth={6} title="treatment.papTherapy.resMedPatient" bottomMargin={false}>
                        {`${searchResult.lastName} ${searchResult.firstName}`}
                      </FormRow>
                      <FormRow headerWidth={6} title="profile.dateOfBirth" bottomMargin={false}>
                        {`${searchResult.dob}`}
                      </FormRow>
                      <br />
                      <FormRow headerWidth={6} title="treatment.papTherapy.deviceSerialNumber" bottomMargin={false}>
                        {`${searchResult.device.serialNo}`}
                      </FormRow>
                      {searchResult.device.deviceType || searchResult.device.deviceTypeDesc ? (
                        <React.Fragment>
                          <FormRow headerWidth={6} title="treatment.papTherapy.deviceModel" bottomMargin={false}>
                            {`${searchResult.device.deviceTypeDesc ?? deviceName ?? '-'}`}
                          </FormRow>
                          <FormRow headerWidth={6} title="treatment.papTherapy.deviceNumber" bottomMargin={false}>
                            {`${searchResult.device.deviceType ?? '-'}`}
                          </FormRow>
                        </React.Fragment>
                      ) : (
                        <FormRow headerWidth={6} title="treatment.papTherapy.deviceNumber" bottomMargin={false}>
                          {`${searchResult.device.deviceNumber ?? '-'}`}
                        </FormRow>
                      )}
                    </div>
                  </StyledContainer>
                  <div style={{ padding: '1rem 0 1rem 0' }}>
                    <Container
                      style={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Item>
                        <ActionButtonRounded
                          text="general.confirm"
                          onClick={() => {
                            linkAvxPatient(searchResult.ecn)
                              .then(() => {
                                void loadAvxPatientLink(dispatch);
                                setDialogOpen(false);
                              })
                              .catch(() => {
                                return;
                              });
                          }}
                          fontSize={16}
                          width={15}
                          height={4}
                          disabled={!patientData}
                          disabledTooltip={fm('treatment.papTherapy.invalidDOBMessage')}
                          // uppercase={false}
                        />
                      </Item>
                      <Item>
                        <ActionButtonRounded
                          text="myService.reject"
                          onClick={() => setState(undefined)}
                          fontSize={16}
                          width={15}
                          height={4}
                          colorScheme="error"
                          // uppercase={false}
                        />
                      </Item>
                    </Container>
                  </div>
                </StyledContainer>
              ) : (
                <></>
              ),
            }[state]
          }
        </React.Fragment>
      ) : (
        <Container style={{ height: '28rem' }} />
      )}
    </React.Fragment>
  );
};

type TSearchState = 'searching' | 'found' | undefined;

type TSearchResult = {
  ecn: TPatient['ecn'];
  firstName: TPatient['firstName'];
  lastName: TPatient['lastName'];
  dob: TPatient['dob'];
  location: TPatient['location'];
  setupDate: TPatient['setupDate'];
  device: TPatient['prescription']['device'];
};

const EditView = ({
  setDialogOpen,
  setDialogState,
  deviceSerialNumber,
  setDeviceSerialNumber,
}: {
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setDialogState: React.Dispatch<React.SetStateAction<TDialogState>>;
  deviceSerialNumber: string;
  setDeviceSerialNumber: React.Dispatch<React.SetStateAction<string>>;
}): React.JSX.Element => {
  const dispatch = useAppDispatch();
  const papTherapyContext = React.useContext(PAPTherapyContext);
  const { openInformationDialog } = papTherapyContext;
  const ecn = useAppSelector((s: IState) => s.session.patientIntegrationDetails?.resMed?.ecn);

  // Used to differentiate editing from creating
  const [editing, setEditing] = React.useState<boolean>(false);
  const [editingDevice, setEditingDevice] = React.useState<boolean>(false);
  const [clearTooltipOpen, setClearTooltipOpen] = React.useState<boolean>(false);

  const { ssn, ssnLocalization, firstNames, lastNames } = useSelector((s: IState) => s.patient.data) ?? {};

  const staticAvxCreatorId: string | null = useAppSelector((s: IState) => {
    const orgSettings = s.settings.orgSettings;
    if (typeof orgSettings.resmed?.staticAvxCreatorId !== 'string') return null;
    return orgSettings.resmed.staticAvxCreatorId;
  });

  const [profileData, setProfileData] = React.useState<Partial<TProfileData>>({});
  const [locations, setLocations] = React.useState<TLocation[]>([]);
  const [avxLocationUsers, setAvxLocationUsers] = React.useState<TAvxLocationUser[]>([]);

  const [loading, setLoading] = React.useState<boolean>(false);

  const handleChange = (e: TOnChangeValues) => {
    const name = Object.keys(e)[0];
    if (!name) return;
    const value = e[name];
    setProfileData({ ...profileData, [name]: value });
  };

  const handleClear = () => {
    setProfileData({
      ...profileData,
      deviceSerial: undefined,
      deviceNumber: undefined,
      deviceType: undefined,
      deviceTypeDesc: undefined,
    });
  };

  const handleClose = (
    action: 'create' | 'edit',
    status: 'success' | 'fail',
    responseText?: string,
    serialNumber?: string,
  ): void => {
    setTimeout(() => {
      if (action === 'create' && status === 'fail') {
        setDialogState('search');
        setDeviceSerialNumber('');
      }
      openInformationDialog(
        <React.Fragment>
          {fm(`treatment.papTherapy.${action}ProfileInformation.${status}`)}
          {responseText && (
            <span>
              :<br />
              {fm(`treatment.papTherapy.responseText.${responseText}`, {
                serialNumber: serialNumber ?? '',
                strong: chunks => (
                  <span key={chunks.join('')} style={{ fontWeight: 600 }}>
                    {React.Children.toArray(chunks)}
                  </span>
                ),
              })}
            </span>
          )}
        </React.Fragment>,
        responseText ? 10000 : true,
      );
    }, 200);
  };

  // Set locations
  React.useEffect(() => {
    fetchLocations()
      .then(locs => {
        if (Array.isArray(locs)) {
          setLocations(locs.filter(l => l.id && l.name));
        }
      })
      .catch(() => {
        return;
      });
  }, []);

  // Set clinical users
  React.useEffect(() => {
    if (staticAvxCreatorId !== null || !profileData.location || locations.length === 0) return;

    const selectedLocation = locations.find(loc => loc.name === profileData.location);
    if (!selectedLocation) return;

    void fetchAvxLocationUsers(selectedLocation.id)
      .then(users => {
        if (users === null) return;
        setAvxLocationUsers(users);
      })
      .catch(() => null);
  }, [profileData.location, staticAvxCreatorId, locations]);

  // Set editing data
  React.useEffect(() => {
    if (ecn && !editing) {
      setEditing(true);
      fetchAvxPatientInfo(ecn)
        .then(res => {
          if (res && typeof res === 'object' && !isEmpty(res)) {
            const data: Partial<TProfileData> = {
              location: res.location,
              setupDate: partialDateFromDate(new Date(res.setupDate)),
              deviceSerial: res.prescription.device.serialNo,
              avxUserId: undefined,
            };

            if (data.deviceSerial) {
              data.deviceType = res.prescription.device.deviceType ?? res.prescription.device.deviceNumber;
              data.deviceTypeDesc = res.prescription.device.deviceTypeDesc;
            } else {
              setEditingDevice(true);
            }

            setProfileData(data);
          }
        })
        .catch(() => {
          return;
        });
    } else {
      setProfileData({
        location: undefined,
        setupDate: nowPartialDate(),
        deviceSerial: deviceSerialNumber,
        deviceNumber: undefined,
        deviceType: undefined,
        deviceTypeDesc: undefined,
        avxUserId: staticAvxCreatorId ?? undefined,
      });
    }
  }, []);

  const isDataValid =
    profileData.location &&
    isPartialDate(profileData.setupDate) &&
    profileData.deviceSerial?.length === 11 &&
    // If creating or if editing device (device has been cleared), require deviceNumber
    (!editing || editingDevice ? profileData.deviceNumber?.length === 3 : true) &&
    (editing || profileData.avxUserId);

  // Data used when creating a new ResMed patient
  const createData = omit(['deviceType', 'deviceTypeDesc'], profileData);

  // Only location, setupDate, deviceSerial and deviceNumber can be updated
  const omittedUpdateDataFields: Array<keyof TProfileData> = (
    ['deviceType', 'deviceTypeDesc', 'avxUserId'] as Array<keyof TProfileData>
  ).concat(profileData.deviceNumber?.length !== 3 ? ['deviceSerial'] : []);
  // Data used when editing an existing ResMed patient
  const updateData = ecn ? { ecn, ...omit(omittedUpdateDataFields, profileData) } : undefined;

  const profileDataHasDeviceTypeOrDesc = !!(profileData.deviceType ?? profileData.deviceTypeDesc);

  // Used if there is a deviceType that should match an S9 device but no deviceTypeDesc
  const deviceName =
    profileData.deviceType &&
    profileData.deviceType.length === 6 &&
    deviceTypesToDeviceNamesMap.has(profileData.deviceType)
      ? deviceTypesToDeviceNamesMap.get(profileData.deviceType)
      : undefined;

  const mounted = 'location' in profileData;

  // Show deviceTypeDesc as deviceModel if editing and device has not been cleared
  const showDeviceTypeDesc = editing && !editingDevice;
  /// Show deviceNumber input if creating or if device has been cleared
  const showDeviceNumber = !editing || editingDevice;

  const demograhics = ssn && ssnLocalization ? parsePatientDemographics(ssnLocalization, { ssn }) : undefined;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        minHeight: ecn ? '65rem' : '70rem',
      }}
    >
      <div>
        <div style={{ marginBottom: '3rem' }}>
          <span>{fm(`treatment.papTherapy.${editing ? 'edit' : 'connectNew'}ResMed`)}</span>
        </div>
        <FormSection style={{ margin: '0 -3rem 0 -3rem', padding: '3rem 3rem', paddingTop: 0, borderTop: 'none' }}>
          <FormRow headerWidth={4} title="treatment.papTherapy.patientName">
            <span style={{ fontWeight: 600 }}>{`${lastNames ?? ''} ${firstNames ?? ''}`}</span>
          </FormRow>
          <FormRow headerWidth={4} title="profile.dateOfBirth">
            <span style={{ fontWeight: 600 }}>
              {demograhics?.dateOfBirth
                ? formatPartialDate(partialDateFromDate(new Date(demograhics.dateOfBirth)))
                : '-'}
            </span>
          </FormRow>
        </FormSection>
        <FormSection style={{ margin: '0 -3rem 0 -3rem', padding: '3rem 3rem' }}>
          <FormRow headerWidth={4} title="treatment.papTherapy.location">
            <InputHandler
              type="Select"
              editing={true}
              name="location"
              formData={{
                onChange: handleChange,
                document: { location: profileData.location },
              }}
              options={locations.map(l => l.name)}
              placeholder="treatment.papTherapy.selectLocation"
            />
          </FormRow>
          {!ecn && staticAvxCreatorId === null && (
            <FormRow headerWidth={4} title="treatment.papTherapy.clinicalUsers">
              <InputHandler
                type="Select"
                editing={true}
                name="avxUserId"
                formData={{
                  onChange: handleChange,
                  document: { avxUserId: profileData.avxUserId },
                }}
                options={avxLocationUsers.map(u => u.id)}
                optionFormatter={o => {
                  const user = avxLocationUsers.find(u => u.id === o);
                  return `${user?.name.lastName} ${user?.name.firstName}`;
                }}
                placeholder="treatment.papTherapy.selectClinicalUser"
                disabled={avxLocationUsers.length === 0}
              />
            </FormRow>
          )}
          <FormRow headerWidth={4} title="treatment.papTherapy.setupDate">
            <InputHandler
              type="PartialDate"
              editing={true}
              name="setupDate"
              formData={{
                onChange: handleChange,
                document: { setupDate: profileData.setupDate },
              }}
              isNotCancellable
            />
          </FormRow>
        </FormSection>
        <FormSection style={{ margin: '0 -3rem 0 -3rem', padding: '3rem 3rem' }}>
          <FormRow headerWidth={4} title="treatment.papTherapy.deviceSerialNumber">
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <InputHandler
                type="TextField"
                editing={mounted && editingDevice}
                name="deviceSerial"
                formData={{
                  onChange: handleChange,
                  document: { deviceSerial: profileData.deviceSerial },
                }}
                format={/^\d{0,11}$/}
                width={23}
                placeholder="treatment.papTherapy.enterSerialNumber"
              />
              {editing && (
                <ToolTip
                  title={fm('treatment.papTherapy.clearResMedDeviceTooltip')}
                  content={
                    <div
                      onBlur={() => setClearTooltipOpen(false)}
                      style={{ visibility: editingDevice ? 'hidden' : undefined }}
                    >
                      <ActionButtonRounded
                        width={12}
                        height={3}
                        fontSize={14}
                        text="treatment.papTherapy.clearResMedDevice"
                        onClick={() => {
                          if (!clearTooltipOpen) {
                            setClearTooltipOpen(true);
                          } else {
                            handleClear();
                            setClearTooltipOpen(false);
                            setEditingDevice(true);
                          }
                        }}
                        colorScheme="error"
                        disabled={
                          !(profileData.deviceSerial && (profileData.deviceNumber ?? profileDataHasDeviceTypeOrDesc))
                        }
                      />
                    </div>
                  }
                  open={clearTooltipOpen}
                />
              )}
            </div>
          </FormRow>
          {showDeviceTypeDesc && (
            <FormRow headerWidth={4} title="treatment.papTherapy.deviceModel">
              <InputHandler
                type="TextField"
                editing={false}
                name="deviceTypeDesc"
                formData={{
                  onChange: handleChange,
                  document: { deviceTypeDesc: profileData.deviceTypeDesc ?? deviceName },
                }}
                width={23}
              />
            </FormRow>
          )}
          {showDeviceNumber && (
            <FormRow headerWidth={4} title="treatment.papTherapy.deviceNumber">
              <InputHandler
                type="TextField"
                editing={mounted}
                name="deviceNumber"
                formData={{
                  onChange: handleChange,
                  document: { deviceNumber: profileData.deviceNumber },
                }}
                format={/^\d{0,3}$/}
                width={23}
                placeholder="treatment.papTherapy.enterDeviceNumber"
              />
            </FormRow>
          )}
        </FormSection>
      </div>
      <Container
        style={{
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '3rem',
        }}
      >
        <Item>
          <ActionButtonRounded
            text="general.cancel"
            onClick={() => {
              if (editing) {
                setDialogOpen(false);
                setTimeout(() => setEditing(false), 400);
              } else {
                setDialogState('search');
              }
            }}
            fontSize={16}
            width={15}
            height={4}
            colorScheme="cancel"
            // uppercase={false}
          />
        </Item>
        <Item style={{ pointerEvents: loading ? 'none' : 'auto' }}>
          <ActionButtonRounded
            text={editing ? 'general.save' : 'treatment.papTherapy.createProfile'}
            onClick={() => {
              setLoading(true);
              if (editing && updateData) {
                updateAvxPatient(updateData)
                  .then(res => {
                    if (res) {
                      void loadAvxPatientLink(dispatch);
                      setDialogOpen(false);
                      handleClose('edit', 'success');
                      setTimeout(() => {
                        setLoading(false);
                        setEditing(false);
                      }, 400);
                    } else {
                      setDialogOpen(false);
                      handleClose('edit', 'fail');
                      setTimeout(() => {
                        setLoading(false);
                        setEditing(false);
                      }, 400);
                    }
                  })
                  .catch(() => {
                    setLoading(false);
                    return;
                  });
              } else {
                createAvxPatient(createData as TProfileData)
                  .then(res => {
                    if (typeof res === 'object' && res !== null && !Array.isArray(res)) {
                      void loadAvxPatientLink(dispatch);
                      setDialogOpen(false);
                      handleClose('create', 'success');
                      setLoading(false);
                    } else {
                      setDialogOpen(false);
                      handleClose('create', 'fail', typeof res === 'string' ? res : undefined, createData.deviceSerial);
                      setLoading(false);
                    }
                  })
                  .catch(() => {
                    setLoading(false);
                    return;
                  });
              }
            }}
            fontSize={16}
            width={15}
            height={4}
            disabled={!isDataValid}
            filled
            loading={loading}
            // uppercase={false}
          />
        </Item>
      </Container>
    </div>
  );
};

// 'start' is currently unused
type TDialogState = 'start' | 'search' | 'edit';

const ResMedDialog = (): React.JSX.Element => {
  const dispatch = useAppDispatch();
  const papTherapyContext = React.useContext(PAPTherapyContext);
  const { resMedDialogOpen, setResMedDialogOpen } = papTherapyContext;
  const integrationEnabled = useAppSelector((s: IState) => !!s.session.patientIntegrationDetails?.resMed);

  const dialogOpen = resMedDialogOpen;
  const setDialogOpen = setResMedDialogOpen;

  const defaultState = integrationEnabled ? 'edit' : 'search';

  const [state, setState] = React.useState<TDialogState>(defaultState);

  const [deviceSerialNumber, setDeviceSerialNumber] = React.useState<string>('');

  React.useEffect(() => {
    if (dialogOpen) void loadAvxPatientLink(dispatch, false);
  }, [dialogOpen, dispatch]);

  return (
    <Dialog
      open={dialogOpen}
      maxWidth={
        {
          start: 'sm',
          search: 'xs',
          edit: 'sm',
        }[state] as Breakpoint
      }
      fullWidth
      PaperProps={{ square: true }}
    >
      <DialogTitle style={dialogTitle}>{fm('treatment.papTherapy.resMedIntegration')}</DialogTitle>
      <DialogContent style={dialogContent}>
        {
          {
            start: <StartView active={integrationEnabled ? true : false} />,
            search: (
              <SearchView
                setDialogOpen={setDialogOpen}
                setDialogState={setState}
                deviceSerialNumber={deviceSerialNumber}
                setDeviceSerialNumber={setDeviceSerialNumber}
              />
            ),
            edit:
              deviceSerialNumber || integrationEnabled ? (
                <EditView
                  setDialogOpen={setDialogOpen}
                  setDialogState={setState}
                  deviceSerialNumber={deviceSerialNumber}
                  setDeviceSerialNumber={setDeviceSerialNumber}
                />
              ) : (
                <></>
              ),
          }[state]
        }
      </DialogContent>
      {state === 'start' && (
        <DialogActions style={dialogActions}>
          <Container justifyContent={'space-between'}>
            <Item>
              <ActionButtonRounded
                text={`treatment.papTherapy.${integrationEnabled ? 'edit' : 'connect'}ResMed`}
                onClick={() => setState(integrationEnabled ? 'edit' : 'search')}
                fontSize={16}
                width={30}
                height={4}
              />
            </Item>
            <Item>
              <ActionButtonRounded
                text="treatment.papTherapy.clearResMedDevice"
                onClick={() => ''}
                fontSize={16}
                width={30}
                height={4}
                colorScheme="error"
                disabled
              />
            </Item>
          </Container>
        </DialogActions>
      )}
      {state === 'search' ? (
        <DialogActions style={{ ...dialogActions, paddingTop: '1rem', paddingBottom: '2rem' }}>
          <ActionButton
            text="header.aboutDialog.close"
            width={10}
            height={3}
            fontSize={16}
            onClick={() => {
              setDeviceSerialNumber('');
              setDialogOpen(false);
              setTimeout(() => setState(defaultState), 500);
            }}
          />
        </DialogActions>
      ) : (
        <div style={{ padding: '1.5rem 0 1.5rem 0' }} />
      )}
    </Dialog>
  );
};

export default ResMedDialog;
